import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Copy, Loader2, ShieldCheck, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TIER_LABEL = { pro: "Pro", team: "Team" };

const STATUS_META = {
  pending: {
    label: "Awaiting payment",
    tone: "text-amber-400",
    dot: "bg-amber-400",
    body: "Send the exact amount below to the platform wallet, then paste the tx hash to start verification.",
  },
  detected: {
    label: "Detected · 0 confirmations",
    tone: "text-sky-400",
    dot: "bg-sky-400",
    body: "Your transaction is in the mempool. We're counting confirmations now — keep this page open.",
  },
  confirmed: {
    label: "Confirmed",
    tone: "text-emerald-400",
    dot: "bg-emerald-400",
    body: "Subscription unlocked. You can return to the dashboard.",
  },
  expired: {
    label: "Expired",
    tone: "text-rose-400",
    dot: "bg-rose-400",
    body: "This invoice expired without a matching payment. Start a fresh checkout to lock in the latest crypto price.",
  },
};

const useCountdown = (expiresAt) => {
  const [remaining, setRemaining] = useState(() => Math.max(0, new Date(expiresAt).getTime() - Date.now()));
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setRemaining(Math.max(0, new Date(expiresAt).getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  return {
    expired: remaining <= 0,
    label: `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`,
  };
};

const CryptoCheckout = () => {
  const { subInvoiceId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tx, setTx] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const promotedRef = useRef(false);

  const load = async () => {
    try {
      const r = await api.get(`/billing/subscription-invoice/${subInvoiceId}`);
      setInv(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load invoice");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subInvoiceId]);

  // Poll the invoice every 8s while it's not in a final state.
  useEffect(() => {
    if (!inv) return;
    if (inv.status === "confirmed" || inv.status === "expired") return;
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv?.status, subInvoiceId]);

  // When confirmed, refresh user (tier flip) once.
  useEffect(() => {
    if (inv?.status === "confirmed" && !promotedRef.current) {
      promotedRef.current = true;
      refresh().catch(() => {});
      toast.success(`Welcome to Quill ${TIER_LABEL[inv.tier] || ""}`);
    }
  }, [inv?.status, inv?.tier, refresh]);

  const { expired: ttlExpired, label: countdownLabel } = useCountdown(inv?.expires_at);

  const submitTx = async () => {
    if (!tx.trim()) {
      toast.error("Paste your transaction hash to start verification");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/billing/subscription-invoice/${subInvoiceId}/submit-tx`, {
        tx_hash: tx.trim(),
      });
      setInv(r.data);
      toast.success("Transaction submitted — counting confirmations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit transaction");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const meta = useMemo(() => {
    if (!inv) return STATUS_META.pending;
    if (inv.status === "pending" && ttlExpired) return STATUS_META.expired;
    return STATUS_META[inv.status] || STATUS_META.pending;
  }, [inv, ttlExpired]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-zinc-400 flex items-center justify-center">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  if (error || !inv) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="checkout-error">
          <AlertTriangle size={28} className="mx-auto text-rose-400" />
          <div className="mt-4 text-lg">{error || "Invoice not found"}</div>
          <button
            onClick={() => navigate("/pricing")}
            data-testid="checkout-back-pricing"
            className="mt-6 px-4 py-2 rounded-md text-sm border border-[#262626] hover:border-white text-zinc-300 hover:text-white"
          >
            Back to pricing
          </button>
        </div>
      </div>
    );
  }

  const chainLabel = inv.chain.toUpperCase();
  const tierLabel = TIER_LABEL[inv.tier] || inv.tier;
  const confirmations = inv.confirmations || 0;
  const required = inv.confirmations_required;
  const progressPct = Math.min(100, Math.round((confirmations / required) * 100));
  const isFinal = inv.status === "confirmed" || inv.status === "expired";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="crypto-checkout-page">
      <div className="px-8 pt-6">
        <button
          onClick={() => navigate("/pricing")}
          data-testid="checkout-back-btn"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={12} /> Back to pricing
        </button>
      </div>

      <div className="px-8 pt-10 pb-4 flex items-baseline gap-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Checkout</div>
        <div className="font-display text-4xl tracking-tighter">
          Quill {tierLabel} · {inv.interval === "annual" ? "Annual" : "Monthly"}
        </div>
      </div>

      <div className="px-8 grid lg:grid-cols-12 gap-8 max-w-6xl pb-16">
        {/* Pay panel */}
        <section className="lg:col-span-7 border border-[#262626] bg-[#0E0E0E] p-6" data-testid="checkout-pay-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              <span data-testid="checkout-status-label" className={meta.tone}>
                {meta.label}
              </span>
            </div>
            {!isFinal && (
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500" data-testid="checkout-countdown">
                Price locked · {countdownLabel}
              </div>
            )}
          </div>

          <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-xl">{meta.body}</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Pay exactly</div>
              <div className="mt-1 font-display text-3xl tracking-tighter" data-testid="checkout-amount-crypto">
                {inv.amount_crypto} {chainLabel}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                = ${inv.amount_usd.toFixed(2)} USD · 1 {chainLabel} = ${inv.crypto_price_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="flex justify-end">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white p-3"
                data-testid="checkout-qr"
              >
                <QRCodeSVG value={inv.pay_to_address} size={120} level="M" />
              </motion.div>
            </div>
          </div>

          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Send to ({chainLabel} platform wallet)
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={inv.pay_to_address}
                data-testid="checkout-pay-address"
                className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-xs font-mono text-zinc-300 focus:outline-none"
              />
              <button
                onClick={() => copy(inv.pay_to_address, "Address")}
                data-testid="checkout-copy-address"
                className="px-3 py-2.5 border border-[#262626] hover:border-white text-zinc-300 hover:text-white inline-flex items-center gap-1 text-xs"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Or copy amount
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={inv.amount_crypto}
                data-testid="checkout-amount-input"
                className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-xs font-mono text-zinc-300 focus:outline-none"
              />
              <button
                onClick={() => copy(String(inv.amount_crypto), "Amount")}
                data-testid="checkout-copy-amount"
                className="px-3 py-2.5 border border-[#262626] hover:border-white text-zinc-300 hover:text-white inline-flex items-center gap-1 text-xs"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>

          {/* TX hash input */}
          {!isFinal && (
            <div className="mt-6 border-t border-[#262626] pt-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
                Step 2 · Paste your transaction hash
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={tx}
                  onChange={(e) => setTx(e.target.value)}
                  placeholder={inv.chain === "btc" ? "BTC txid" : "0x… tx hash"}
                  data-testid="checkout-tx-input"
                  className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-xs font-mono text-zinc-300 focus:outline-none focus:border-white"
                />
                <button
                  onClick={submitTx}
                  disabled={submitting || ttlExpired}
                  data-testid="checkout-submit-tx-btn"
                  className="bg-white text-black text-sm font-medium px-5 py-2.5 rounded-md hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  Verify payment
                </button>
              </div>
              <div className="mt-2 text-[11px] text-zinc-500">
                We poll your tx on-chain every minute. Subscription activates after{" "}
                <strong className="text-zinc-300">
                  {required} {chainLabel} confirmation{required > 1 ? "s" : ""}
                </strong>
                .
              </div>
            </div>
          )}

          {inv.status === "confirmed" && (
            <div className="mt-6 border-t border-[#262626] pt-6 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="checkout-go-dashboard"
                className="bg-white text-black text-sm font-medium px-5 py-2.5 rounded-md hover:bg-zinc-200 inline-flex items-center gap-2"
              >
                <Sparkles size={14} /> Open dashboard
              </button>
            </div>
          )}

          {inv.status === "expired" && (
            <div className="mt-6 border-t border-[#262626] pt-6">
              <button
                onClick={() => navigate("/pricing")}
                data-testid="checkout-restart"
                className="bg-white text-black text-sm font-medium px-5 py-2.5 rounded-md hover:bg-zinc-200"
              >
                Start a new checkout
              </button>
            </div>
          )}
        </section>

        {/* Status + safety panel */}
        <aside className="lg:col-span-5 space-y-6">
          <div className="border border-[#262626] p-6" data-testid="checkout-confirmations-card">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Confirmations</div>
            <div className="font-display text-4xl tracking-tighter" data-testid="checkout-confirmations-count">
              {confirmations}
              <span className="text-zinc-600 text-2xl"> / {required}</span>
            </div>
            <div className="mt-3 h-1.5 bg-[#1A1A1A] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.5 }}
                className={`h-full ${inv.status === "confirmed" ? "bg-emerald-400" : "bg-sky-400"}`}
              />
            </div>
            {inv.tx_hash && (
              <div className="mt-4 text-[11px] text-zinc-500 break-all font-mono" data-testid="checkout-tx-hash">
                tx: {inv.tx_hash}
              </div>
            )}
          </div>

          <div className="border border-[#262626] p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">How activation works</div>
            <ol className="space-y-3 text-xs text-zinc-400 leading-relaxed">
              <li className="flex gap-3">
                <span className="text-zinc-600">01</span>
                <span>Send the exact amount in {chainLabel} to the platform wallet above.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-zinc-600">02</span>
                <span>Paste your transaction hash. We mark the invoice <em>Detected</em> immediately at 0 confirmations.</span>
              </li>
              <li className="flex gap-3">
                <span className="text-zinc-600">03</span>
                <span>
                  Quill {tierLabel} unlocks strictly after <strong className="text-zinc-200">
                  {required} {chainLabel} confirmation{required > 1 ? "s" : ""}</strong>.
                </span>
              </li>
            </ol>
          </div>

          <div className="border border-[#262626] p-6 flex items-start gap-3">
            <ShieldCheck size={16} className="mt-0.5 text-emerald-400 shrink-0" />
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              Quill never touches your client invoice wallet. Subscription payments go to the platform wallet shown above; your earnings stay non-custodial in your own wallet.
            </div>
          </div>

          {inv.status === "confirmed" && (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-6 flex items-start gap-3" data-testid="checkout-success-card">
              <Check size={16} className="mt-0.5 text-emerald-400 shrink-0" />
              <div className="text-xs text-emerald-300 leading-relaxed">
                Payment confirmed on-chain. Quill {tierLabel} is now active on your account.
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CryptoCheckout;
